import { getSupabaseClient } from './supabase';
import { markRequest } from '../lib/actionFailureSignals';
import { AppError } from '../utils/appErrors';
import {
  isHolidayThemeKey,
  parseHolidayThemeState,
  type HolidayThemeKey,
  type HolidayThemeState,
} from '../domain/holidayThemes';

export type HolidayThemeSchedule = {
  id: string;
  themeKey: HolidayThemeKey;
  startsAt: string;
  endsAt: string;
  updatedAt: string;
  updatedByName: string | null;
};

// holiday_theme_schedule_save and _delete write these refusals for people (an overlap names the other
// window in Eastern time), so they are shown as they are; anything else goes through the app's safe text.
const READABLE_REFUSALS = new Set(['23P01', '22023', 'PT409']);
function scheduleError(error: unknown): unknown {
  const { code, message } = (error ?? {}) as { code?: unknown; message?: unknown };
  if (typeof code !== 'string' || !READABLE_REFUSALS.has(code) || typeof message !== 'string' || !message.trim()) return error;
  return new AppError(code === 'PT409' ? 'conflict' : 'validation', message, { code, cause: error });
}

export const holidayThemeCurrentKey = ['holiday-theme-current'] as const;
export const holidayThemeSchedulesKey = ['holiday-theme-schedules'] as const;

function parseSchedule(value: unknown): HolidayThemeSchedule | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (typeof row.id !== 'string' || !isHolidayThemeKey(row.theme_key) || typeof row.starts_at !== 'string'
    || typeof row.ends_at !== 'string' || typeof row.updated_at !== 'string') return null;
  return {
    id: row.id,
    themeKey: row.theme_key,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    updatedAt: row.updated_at,
    updatedByName: typeof row.updated_by_name === 'string' ? row.updated_by_name : null,
  };
}

/** Readable by everyone, signed in or not. GET keeps it a read inside an access preview too. */
export async function fetchHolidayThemeCurrent(): Promise<HolidayThemeState | null> {
  const { data, error } = await markRequest(getSupabaseClient().rpc('holiday_theme_current', {}, { get: true }), 'read');
  if (error) throw error;
  return parseHolidayThemeState(data);
}

export async function fetchHolidayThemeSchedules(): Promise<HolidayThemeSchedule[]> {
  const { data, error } = await markRequest(getSupabaseClient().rpc('holiday_theme_schedules_list'), 'read');
  if (error) throw error;
  if (!Array.isArray(data)) throw new Error('The holiday theme schedule could not be read.');
  return data.map(parseSchedule).filter((row): row is HolidayThemeSchedule => row !== null);
}

export async function saveHolidayThemeSchedule(params: {
  id: string;
  expectedUpdatedAt: string | null;
  themeKey: HolidayThemeKey;
  startsAt: string;
  endsAt: string;
}): Promise<HolidayThemeSchedule> {
  const { data, error } = await markRequest(getSupabaseClient().rpc('holiday_theme_schedule_save', {
    p_schedule_id: params.id,
    p_expected_updated_at: params.expectedUpdatedAt,
    p_theme_key: params.themeKey,
    p_starts_at: params.startsAt,
    p_ends_at: params.endsAt,
  }), 'mutation', { handlesVersionConflict: true });
  if (error) throw scheduleError(error);
  const saved = parseSchedule(data);
  if (!saved) throw new Error('The saved holiday theme was not acknowledged. Reload before trying again.');
  return saved;
}

export async function deleteHolidayThemeSchedule(id: string, expectedUpdatedAt: string): Promise<void> {
  const { error } = await markRequest(getSupabaseClient().rpc('holiday_theme_schedule_delete', {
    p_schedule_id: id,
    p_expected_updated_at: expectedUpdatedAt,
  }), 'mutation', { handlesVersionConflict: true });
  if (error) throw scheduleError(error);
}
